import apiInstance from './apiInstance';

async function logout() {
  try {
    return await apiInstance.post('/account/logout');
  } catch (error) {
    console.log(error);
    return null;
  }
}

async function handleUnauth(error) {
  if (error.response && error.response.status === 401) {
    await logout();
    window.location.reload();
  } else {
    throw error;
  }
}

function buildAssignQuery(applicationType, isManagerUser) {
  const params = new URLSearchParams();
  if (applicationType) params.set('applicationType', applicationType);
  if (isManagerUser) params.set('isManagerUser', '1');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

async function assignLandlordUser(
  id,
  newUserId,
  applicationType = '',
  isManagerUser = false
) {
  try {
    const query = buildAssignQuery(applicationType, isManagerUser);
    const res = await apiInstance.put(
      `/landlords/assign-user/${id}/${newUserId}${query}`
    );

    return res.data;
  } catch (error) {
    return handleUnauth(error);
  }
}

async function assignSecondaryLandlordUser(
  id,
  newUserId,
  applicationType = '',
  isManagerUser = false
) {
  try {
    const query = buildAssignQuery(applicationType, isManagerUser);
    const res = await apiInstance.put(
      `/landlords/assign-secondary-user/${id}/${newUserId}${query}`
    );

    return res.data;
  } catch (error) {
    return handleUnauth(error);
  }
}

async function unassignLandlordUser(
  id,
  applicationType = '',
  isManagerUser = false
) {
  try {
    const query = buildAssignQuery(applicationType, isManagerUser);
    const res = await apiInstance.put(`/landlords/unassign-user/${id}${query}`);

    return res.data;
  } catch (error) {
    return handleUnauth(error);
  }
}

async function unassignSecondaryLandlordUser(
  id,
  applicationType = '',
  isManagerUser = false
) {
  try {
    const query = buildAssignQuery(applicationType, isManagerUser);
    const res = await apiInstance.put(
      `/landlords/unassign-secondary-user/${id}${query}`
    );

    return res.data;
  } catch (error) {
    return handleUnauth(error);
  }
}

async function login(credentials) {
  try {
    const res = await apiInstance.post('/account/login', credentials);
    return res;
  } catch (error) {
    return null;
  }
}

async function changePassword(newPassword, uuid) {
  return await apiInstance.post(`/account/new-password/${uuid}`, {
    password: newPassword
  });
}

async function requestPasswordReset(username) {
  return await apiInstance.post(`/account/reset-password/`, {
    username: username
  });
}

async function getAccount() {
  try {
    const { data } = await apiInstance.get('/account/');
    return data;
  } catch (error) {
    console.log('Unauthorized');
  }
  return null;
}

async function getLandlordUsers(applicationId, moduleType) {
  try {
    const { data } = await apiInstance.get(
      `/landlords/users/${applicationId}?moduleType=${moduleType}`
    );
    return data;
  } catch (error) {
    return handleUnauth(error);
  }
}

async function getTutorial(path) {
  try {
    const res = await apiInstance.get(path);
    return res.data;
  } catch (error) {
    return handleUnauth(error);
  }
}

async function putTutorial(path, data) {
  try {
    return await apiInstance.put(path, data);
  } catch (error) {
    return handleUnauth(error);
  }
}

async function getApplicationLog(applicationId, ah) {
  const res = await apiInstance.get(
    `/logs/applications/${applicationId}${ah ? '?ah=true' : ''}`
  );
  return res.data;
}

async function storeLandlordFile(applicationId, file) {
  const formData = new FormData();
  formData.append('applicationId', applicationId);
  formData.append('file', file);

  const { data } = await apiInstance.put(
    `/landlords/files/${applicationId}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }
  );

  return data;
}

async function getLandlordFiles(applicationId) {
  const { data } = await apiInstance.get(`/landlords/files/${applicationId}`);
  return data;
}

async function deleteLandlordFile(applicationId, fileName) {
  return await apiInstance.delete(`/landlords/files/${applicationId}`, {
    data: {
      fileName: fileName
    }
  });
}

async function getAvailableTags(type = 'ba') {
  const { data } = await apiInstance.get(`/tags?type=${type}`);
  return data;
}

async function addTag(applicationId, tagId) {
  const { data } = await apiInstance.put(`/tags/${applicationId}/${tagId}`);
  return data;
}

async function removeTag(applicationId, tagId) {
  const { data } = await apiInstance.delete(`/tags/${applicationId}/${tagId}`);
  return data;
}

async function mergeAhOvDocuments({
  applicationId,
  documentIds = [],
  landlordDocs = [],
  attachmentsIds = [],
  scrive = false,
  abortController
}) {
  const res = await apiInstance.post(
    `/andrahand/portal/documents/pdf/merge/${applicationId}`,
    {
      landlordDocs,
      documentIds,
      attachmentsIds,
      scrive
    },
    { signal: abortController?.signal, responseType: 'blob' }
  );
  return res.data;
}

async function getNotes(applicationId, types = null) {
  const url = new URL(`/notes/${applicationId}`, apiInstance.defaults.baseURL);

  if (types && types.length) {
    types.forEach(t => url.searchParams.append('types', t));
  }

  const res = await apiInstance.get(url.pathname + url.search);
  return res.data;
}

async function saveNote(applicationId, type, note) {
  try {
    const { data } = await apiInstance.post(`/notes`, {
      applicationId,
      type,
      note
    });

    return data;
  } catch (error) {
    return null;
  }
}

async function editNote(applicationId, note, noteId) {
  const { data } = await apiInstance.post(`/notes/edit/${applicationId}`, {
    note,
    noteId
  });

  return data;
}

async function deleteNote(applicationId, noteId) {
  const { data } = await apiInstance.delete(
    `/notes/${applicationId}/${noteId}`
  );

  return data;
}

async function saveManagerNote(applicationId, landlordId, type, note) {
  try {
    const { data } = await apiInstance.post(`/notes/manager/${landlordId}`, {
      applicationId,
      type,
      note
    });

    return data;
  } catch (error) {
    return null;
  }
}

async function editManagerNote(applicationId, landlordId, note, noteId) {
  const { data } = await apiInstance.post(
    `/notes/manager/edit/${applicationId}/${landlordId}`,
    {
      note,
      noteId
    }
  );

  return data;
}

async function deleteManagerNote(applicationId, landlordId, noteId) {
  const { data } = await apiInstance.delete(
    `/notes/manager/${applicationId}/${landlordId}/${noteId}`
  );

  return data;
}

async function addCheck(applicationId, memberId, type) {
  try {
    const { data } = await apiInstance.post(
      `/applications/${applicationId}/check/${memberId}/${type}`
    );
    return data;
  } catch (error) {
    return null;
  }
}

async function removeCheck(applicationId, memberId, type) {
  try {
    const { data } = await apiInstance.delete(
      `/applications/${applicationId}/check/${memberId}/${type}`
    );
    return data;
  } catch (error) {
    return null;
  }
}

async function getUserPreferences() {
  const { data } = await apiInstance.get('/account/preferences');
  return data;
}

async function putNotifyOnCaseRemoval(value) {
  await apiInstance.put(`/account/preferences/notifyOnCaseRemoval/${value}`);
}

async function putNotifyOnCaseAssignment(value) {
  await apiInstance.put(`/account/preferences/notifyOnCaseAssignment/${value}`);
}

async function readNotes(applicationId) {
  await apiInstance.post(`/notes/read/${applicationId}`);
}

export {
  logout,
  handleUnauth,
  assignLandlordUser,
  assignSecondaryLandlordUser,
  unassignLandlordUser,
  unassignSecondaryLandlordUser,
  login,
  changePassword,
  requestPasswordReset,
  getAccount,
  putNotifyOnCaseRemoval,
  putNotifyOnCaseAssignment,
  getLandlordUsers,
  getTutorial,
  putTutorial,
  getApplicationLog,
  storeLandlordFile,
  getLandlordFiles,
  deleteLandlordFile,
  getAvailableTags,
  addTag,
  removeTag,
  mergeAhOvDocuments,
  getNotes,
  saveNote,
  editNote,
  deleteNote,
  saveManagerNote,
  editManagerNote,
  deleteManagerNote,
  addCheck,
  removeCheck,
  getUserPreferences,
  readNotes
};
